// AI対戦のレベル表・解放の進み具合・AIの呼び出し（メインスレッド側）。
// 探索そのものは Web Worker で動く（自作AI = ai-worker.js、Marcher = marcher-worker.js）。
// Node（tools/arena.mjs・テスト）からはレベル表と解放の計算だけを使う。

(function (root, factory) {
    if (typeof module === 'object' && module.exports) module.exports = factory();
    else root.CheckersAI = factory();
})(typeof self !== 'undefined' ? self : this, function () {
    'use strict';

    // 強さは「読みの深さ」で固定する（端末の速さで強さが変わらない）。
    // noise は指し手のばらつき（手加減）。最終深さでの各手の評価に ±noise の乱数を足してから選ぶので、
    // 100 = 通常駒1枚ぶんの揺れ。Easy〜Expert は「読みの浅さ」だけでなくこれでも弱くしている。
    // 2026-09 の実績（人の勝率 Easy 56%・Medium 15%）から全体を下げた: Medium = 旧Easy、Expert = 旧Medium。
    // Marcher の time は秒。遅い端末向けの保険で、通常はそこまでかからない（Mac 実測: 深さ12 は対局中 平均53ms・最大733ms）。
    const LEVELS = [
        { id: 'easy',         engine: 'local',   depth: 1,  noise: 2000 },
        { id: 'medium',       engine: 'local',   depth: 1,  noise: 700 },
        { id: 'hard',         engine: 'local',   depth: 1,  noise: 350 },
        { id: 'expert',       engine: 'local',   depth: 2,  noise: 300 },
        { id: 'master',       engine: 'marcher', depth: 1,  time: 4 },
        { id: 'grandmaster',  engine: 'marcher', depth: 2,  time: 4 },
        { id: 'luminary',     engine: 'marcher', depth: 3,  time: 4 },
        { id: 'transcendent', engine: 'marcher', depth: 4,  time: 4 },
        { id: 'legend',       engine: 'marcher', depth: 6,  time: 5 },
        { id: 'legend2',      engine: 'marcher', depth: 8,  time: 6 },
        { id: 'legend3',      engine: 'marcher', depth: 12, time: 5 }
    ];
    const LEVEL_IDS = LEVELS.map(l => l.id);
    const DEFAULT_LEVEL = 'medium';
    const FIRST_LOCKED = 'master';   // ここから上は、1つ下のレベルに勝つと解放
    const FALLBACK_LEVEL = 'expert'; // Marcher が使えないときに代わりに指すレベル
    const HINT_DEPTH = 8;
    // 1手の探索ノード上限。取り合いが続く局面ではノード数が膨らむことがあるので、
    // 上限に当たったら1つ浅い深さの結果を使う（体感で数秒待たせないため）
    const LOCAL_MAX_NODES = 600000;
    const HINT_MAX_NODES = 400000;

    function levelOf(id) { return LEVELS.find(l => l.id === id) || LEVELS.find(l => l.id === DEFAULT_LEVEL); }
    function levelIndex(id) { return LEVEL_IDS.indexOf(id); }
    function normalizeLevel(id) { return LEVEL_IDS.includes(id) ? id : DEFAULT_LEVEL; }
    function usesMarcher(id) { return levelOf(id).engine === 'marcher'; }
    function nextLevel(id) { const i = levelIndex(id); return i >= 0 && i + 1 < LEVELS.length ? LEVEL_IDS[i + 1] : null; }
    function previousLevel(id) { const i = levelIndex(id); return i > 0 ? LEVEL_IDS[i - 1] : null; }

    // 進み具合 {cleared: {level: true}}。解放は「1つ下のレベルに勝ったか」で決まるので別に持たない
    function normalizeProgress(raw) {
        const cleared = {};
        if (raw && typeof raw === 'object' && raw.cleared && typeof raw.cleared === 'object') {
            for (const id of LEVEL_IDS) if (raw.cleared[id] === true) cleared[id] = true;
        }
        return { cleared };
    }
    function isCleared(progress, id) { return Boolean(progress.cleared[id]); }
    function isUnlocked(progress, id) {
        const i = levelIndex(id);
        if (i < 0) return false;
        if (i < levelIndex(FIRST_LOCKED)) return true;
        return isCleared(progress, LEVEL_IDS[i - 1]);
    }
    // 一覧に出すのは解放済みと「次に解放される1つ」だけ（それより上は解放するまで見せない）
    function isVisible(progress, id) {
        const i = levelIndex(id);
        return i >= 0 && (isUnlocked(progress, id) || isUnlocked(progress, LEVEL_IDS[i - 1]));
    }
    // 勝ったときに呼ぶ。新しく解放されたレベルの id（無ければ null）を返す
    function recordWin(progress, id) {
        const wasUnlockedNext = nextLevel(id) ? isUnlocked(progress, nextLevel(id)) : true;
        progress.cleared[id] = true;
        const next = nextLevel(id);
        return next && !wasUnlockedNext && isUnlocked(progress, next) ? next : null;
    }

    // ---- ブラウザ側：Worker の呼び出し ---------------------------------------
    // requestMove / requestHint は Promise を返す。cancel() 以降に届いた古い結果は捨てる。
    class AIController {
        constructor({ onMarcherStatus } = {}) {
            this.level = DEFAULT_LEVEL;
            this.onMarcherStatus = onMarcherStatus || (() => {});
            this.localWorker = null;
            this.marcherWorker = null;
            this.marcherReady = null;   // Promise（読み込み中）。失敗したら null に戻す
            this.marcherFailed = false;
            this.pending = new Map();
            this.nextId = 1;
            this.generation = 0;
        }

        setLevel(id) { this.level = normalizeLevel(id); }
        get profile() { return levelOf(this.level); }
        usesMarcher() { return usesMarcher(this.level) && !this.marcherFailed; }

        ensureLocalWorker() {
            if (!this.localWorker) {
                const worker = new Worker('/ai-worker.js');
                worker.onmessage = e => this.onMessage(e.data);
                // 読み込み失敗（回線断など）は捨てて、次の要求で作り直す
                worker.onerror = e => {
                    this.failAll(new Error(e.message || 'ai worker failed'));
                    if (this.localWorker === worker) { worker.terminate(); this.localWorker = null; }
                };
                this.localWorker = worker;
            }
            return this.localWorker;
        }

        // Marcher（WASM 約190KB）は Master 以上を選んだときだけ読み込む
        preloadMarcher() {
            if (this.marcherFailed) return Promise.reject(new Error('marcher unavailable'));
            if (this.marcherReady) return this.marcherReady;
            if (typeof WebAssembly === 'undefined' || typeof BigInt !== 'function') {
                this.marcherFailed = true;
                return Promise.reject(new Error('marcher unsupported'));
            }
            this.onMarcherStatus('loading');
            const worker = new Worker('/marcher-worker.js');
            this.marcherWorker = worker;
            worker.onmessage = e => this.onMessage(e.data);
            const id = this.nextId++;
            this.marcherReady = new Promise((resolve, reject) => {
                this.pending.set(id, { resolve, reject, generation: -1 });
                worker.onerror = e => {
                    this.failAll(new Error(e.message || 'marcher worker failed'));
                    reject(new Error(e.message || 'marcher worker failed'));
                };
                worker.postMessage({ type: 'init', id });
            }).then(() => {
                this.onMarcherStatus('ready');
            }).catch(err => {
                this.marcherFailed = true;
                this.marcherReady = null;
                this.disposeMarcher();
                this.onMarcherStatus('failed');
                throw err;
            });
            return this.marcherReady;
        }

        onMessage(m) {
            if (!m || !this.pending.has(m.id)) return;
            const p = this.pending.get(m.id);
            this.pending.delete(m.id);
            if (m.type === 'error') p.reject(new Error(m.message || 'engine error'));
            else p.resolve(m);
        }

        failAll(error) {
            for (const p of this.pending.values()) p.reject(error);
            this.pending.clear();
        }

        post(worker, message) {
            const id = this.nextId++;
            const generation = this.generation;
            return new Promise((resolve, reject) => {
                this.pending.set(id, { resolve, reject, generation });
                worker.postMessage({ ...message, id });
            }).then(result => {
                if (generation !== this.generation) throw Object.assign(new Error('cancelled'), { cancelled: true });
                return result;
            });
        }

        // 現在のレベルで1手を返す。Marcher が使えなければ Expert（自作AI）で代わりに指し、engine に 'fallback' を入れる
        async requestMove(state, forceCapture) {
            const profile = this.profile;
            if (profile.engine === 'marcher' && !this.marcherFailed) {
                try {
                    await this.preloadMarcher();
                    const res = await this.post(this.marcherWorker, { type: 'search', state, depth: profile.depth, time: profile.time });
                    if (!res.move) throw new Error('marcher returned no move');
                    return { move: res.move, engine: 'marcher', eval: res.eval, depth: res.depth };
                } catch (err) {
                    if (err && err.cancelled) throw err;
                    this.marcherFailed = true;
                    this.disposeMarcher();
                    this.onMarcherStatus('failed');
                }
            }
            const local = profile.engine === 'local' ? profile : levelOf(FALLBACK_LEVEL);
            const res = await this.post(this.ensureLocalWorker(), {
                type: 'search', state,
                options: { depth: local.depth, noise: local.noise, forceCapture, maxNodes: LOCAL_MAX_NODES }
            });
            return { move: res.move, engine: profile.engine === 'local' ? 'local' : 'fallback', score: res.score, depth: res.depth };
        }

        async requestHint(state, forceCapture) {
            const res = await this.post(this.ensureLocalWorker(), {
                type: 'search', state, options: { depth: HINT_DEPTH, noise: 0, forceCapture, maxNodes: HINT_MAX_NODES }
            });
            return res.move;
        }

        // 進行中の探索の結果を捨てる（待った・新規対局・レベル変更）
        cancel() {
            this.generation++;
            // Worker 側の探索は止められないので、結果が届いても捨てるだけ（次の探索はその後に処理される）
        }

        resetLocal() {
            if (this.localWorker) this.localWorker.postMessage({ type: 'reset' });
        }

        disposeMarcher() {
            if (this.marcherWorker) {
                this.marcherWorker.terminate();
                this.marcherWorker = null;
            }
            this.marcherReady = null;
        }

        dispose() {
            this.cancel();
            this.failAll(Object.assign(new Error('disposed'), { cancelled: true }));
            if (this.localWorker) { this.localWorker.terminate(); this.localWorker = null; }
            this.disposeMarcher();
        }
    }

    return {
        LEVELS, LEVEL_IDS, DEFAULT_LEVEL, FIRST_LOCKED, FALLBACK_LEVEL, HINT_DEPTH,
        levelOf, levelIndex, normalizeLevel, usesMarcher, nextLevel, previousLevel,
        normalizeProgress, isCleared, isUnlocked, isVisible, recordWin,
        AIController
    };
});
